// shared/src/schemas/qa-scoreable-claim.ts
// Q&A Calibration (docs/qa-calibration-2026-06-16-brief-revision.md) — the
// structured, machine-comparable claim the q-qa 1.6.0 writer emits inside
// /predicted so the macro-prediction-scorer can grade the answer against
// actuals at its horizon. Today Q&A answers are free-text + never scored.
//
// kind routes the scorer's HYBRID grading (decided 2026-06-16):
//   · 'price'       → deterministic: realized close for `subject` (a ticker)
//                     vs target_value/direction/comparator within the window.
//   · 'macro'       → deterministic: realized macro indicator (`subject`) the
//                     same way, via the scorer's FRED-grounding loader.
//   · 'qualitative' → LLM judge (q-qa-judge): target_value is null; the judge
//                     reads predicted_answer + realized context and verdicts.
//
// Enum coercion mirrors the repo-wide translate-in-code rule
// ([[feedback_llm_enum_coerce]]): the model emits its natural value, a resolver
// maps known synonyms to canonical, an UNKNOWN value still fail-louds. The
// SCOREABLE_CLAIM field itself is OPTIONAL on the q-qa OutputSchema — a missing
// claim must NOT poison the whole answer (same lesson as the C031 question_id
// echo); the answer persists with scoreable_claim_json=NULL and is simply not
// scored (forward) until the gated backfill routes it through the judge.

import { z } from 'zod';
import { coercedEnum, makeEnumCoercer } from '../resolvers/llm-enum-coerce.js';

// ── kind ──────────────────────────────────────────────────────────────────
export const coerceQaClaimKind = makeEnumCoercer({
  price: 'price', equity: 'price', stock: 'price', etf: 'price', ticker: 'price',
  level: 'price', 'price-level': 'price',
  macro: 'macro', indicator: 'macro', economic: 'macro', econ: 'macro', fred: 'macro',
  series: 'macro', rate: 'macro',
  qualitative: 'qualitative', qual: 'qualitative', regime: 'qualitative',
  narrative: 'qualitative', event: 'qualitative', categorical: 'qualitative',
  boolean: 'qualitative', yesno: 'qualitative', 'yes/no': 'qualitative',
});
export const QaClaimKindEnum = coercedEnum(['price', 'macro', 'qualitative'], coerceQaClaimKind);

// ── direction (up/down/flat + na) ───────────────────────────────────────────
export const coerceQaClaimDirection = makeEnumCoercer({
  up: 'up', bull: 'up', bullish: 'up', rise: 'up', rising: 'up', higher: 'up',
  positive: 'up', gain: 'up', upside: 'up',
  down: 'down', bear: 'down', bearish: 'down', fall: 'down', falling: 'down',
  lower: 'down', negative: 'down', decline: 'down', drop: 'down', downside: 'down',
  flat: 'flat', neutral: 'flat', sideways: 'flat', unchanged: 'flat',
  rangebound: 'flat', 'range-bound': 'flat', stable: 'flat', steady: 'flat',
  na: 'na', 'n/a': 'na', none: 'na', unknown: 'na', any: 'na', 'not applicable': 'na',
});
export const QaClaimDirectionEnum = coercedEnum(['up', 'down', 'flat', 'na'], coerceQaClaimDirection);

// ── comparator (how target relates to the realized value at horizon) ────────
//   lte   — realized ≤ target counts as hit (e.g. "4-week LOW ≤ $580")
//   gte   — realized ≥ target counts as hit (e.g. "4-week HIGH ≥ $620")
//   range — realized within [range_low, range_high]
//   cross — realized crossed `target_value` in `direction` at any point (path)
//   na    — qualitative; no numeric comparator
export const coerceQaClaimComparator = makeEnumCoercer({
  lte: 'lte', 'le': 'lte', 'less_than_or_equal': 'lte', 'at_or_below': 'lte', below: 'lte',
  'at-or-below': 'lte', 'less-equal': 'lte', max: 'lte', floor: 'lte',
  gte: 'gte', 'ge': 'gte', 'greater_than_or_equal': 'gte', 'at_or_above': 'gte', above: 'gte',
  'at-or-above': 'gte', 'greater-equal': 'gte', min: 'gte', ceiling: 'gte',
  range: 'range', band: 'range', between: 'range', interval: 'range',
  cross: 'cross', crosses: 'cross', touch: 'cross', breach: 'cross', reaches: 'cross',
  na: 'na', 'n/a': 'na', none: 'na', 'not applicable': 'na',
});
export const QaClaimComparatorEnum = coercedEnum(['lte', 'gte', 'range', 'cross', 'na'], coerceQaClaimComparator);

// ── tolerant nullable number (LLM may emit "$580", "1,234", "n/a") ──────────
function coerceNullableNumber(v: unknown): unknown {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[$,%\s]/g, '');
    if (cleaned === '' || /^(n\/?a|null|none|tbd)$/i.test(cleaned)) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : v; // genuinely non-numeric → z.number().nullable() rejects (fail-loud)
  }
  return v;
}
const NullableNumber = z.preprocess(coerceNullableNumber, z.number().nullable());

// ── horizon_days (the horizon the QUESTION names; the LLM already commits a
//    number at it per q-qa RULE 1). Tolerate a string emit. ──────────────────
function coerceHorizonDays(v: unknown): unknown {
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? Math.round(n) : v;
  }
  return v;
}

// Directional lean of each of the FOUR perspectives, for perspective
// calibration (D2b). Cheap enums the LLM already knows from its own 4 answers;
// the scorer compares each lean to the REALIZED direction in code (no extra LLM
// call on the deterministic path). Each field optional → treated as 'na' when
// absent so a partial emit never poisons the answer.
const QaPerspectiveLeansSchema = z.object({
  consensus:  QaClaimDirectionEnum,
  contrarian: QaClaimDirectionEnum,
  trajectory: QaClaimDirectionEnum,
  predicted:  QaClaimDirectionEnum,
}).partial();

export const QaScoreableClaimSchema = z.object({
  kind:         QaClaimKindEnum,
  subject:      z.string().min(1),  // price → ticker; macro → indicator/FRED series; qualitative → short echo
  direction:    QaClaimDirectionEnum,
  comparator:   QaClaimComparatorEnum,
  target_value: NullableNumber,     // null only when kind='qualitative'
  range_low:    NullableNumber,
  range_high:   NullableNumber,
  horizon_days: z.preprocess(coerceHorizonDays, z.number().int().positive()),
  // D2b perspective calibration — the directional lean of each perspective.
  // OPTIONAL (whole object + each field); scorer defaults missing leans to 'na'.
  perspective_leans: QaPerspectiveLeansSchema.optional(),
}).passthrough();

export type QaScoreableClaim = z.infer<typeof QaScoreableClaimSchema>;
